import * as Yup from 'yup';
import { TFunction } from 'i18next';

// The rules for a dashboard's title and ID, shared by the dashboard form and the "Save as new" panel so they cannot drift apart.
// Not a React component/hook, so callers pass their own `t` from useTranslation().

const ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

interface IdOwner {
  id?: number;
  dashboardId?: string;
}

/**
 * A dashboard ID: required, only letters, numbers, - and _, and not used by another dashboard (ignoring case and spaces).
 * `excludeId` is the dashboard being edited, so it does not clash with itself; leave it out when making a new one,
 * for example a copy, where every existing dashboard counts as taken.
 */
export const dashboardIdSchema = (dashboards: IdOwner[], t: TFunction, excludeId?: number) => {
  // Plain strings: passing t() straight into Yup's overloads trips a TS "excessively deep" error.
  const required: string = t('VALIDATION.DASHBOARD_ID_REQUIRED');
  const invalidChars: string = t('VALIDATION.DASHBOARD_ID_INVALID_CHARS');
  const duplicate: string = t('VALIDATION.DASHBOARD_ID_DUPLICATE');
  return Yup.string()
    .required(required)
    .matches(ID_PATTERN, invalidChars)
    .test('unique-dashboard-id', duplicate, (value) => {
      if (!value) return true;
      const wanted = value.trim().toLowerCase();
      return !dashboards.some((d) => (excludeId === undefined || d.id !== excludeId) && (d.dashboardId || '').trim().toLowerCase() === wanted);
    });
};

/** A dashboard title: required, and no longer than `maxLength`. */
export const dashboardTitleSchema = (t: TFunction, maxLength: number) => {
  const required: string = t('VALIDATION.DASHBOARD_TITLE_REQUIRED');
  const tooLong: string = t('VALIDATION.MAX_LENGTH', { n: maxLength });
  return Yup.string().required(required).max(maxLength, tooLong);
};
